/**
 * Games 组件 - 折线图和表格的共用组件
 * @description 提供折线图、表格交互，不包含外层卡片和指标切换
 */
import React, { useMemo, useState, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import Loading from '@intelligence/components/Loading';
import ChartSection from '../ChartSection';
import PublisherTable from '../PublisherTable';
import useGetState from '@intelligence/hooks/useGetState';
import useCancelRequest, { IRequestToken } from '@intelligence/hooks/useCancelRequest';
import useLocalTableState, { createDefaultSorts } from '../../hooks/useLocalTableState';
import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { multiRatioMetricSort } from '@intelligence/v3/utils/metric';
import {
  filterRevenueMetrics,
  formatTooltipTimeRange,
  getTopNGameIds,
  normalizeMetricSortState,
  getSortOrderBys,
} from '../../utils';
import {
  buildAggregatedChartData,
  buildLineChartData,
  createMetricNameFormatter,
  createXAxisLabelFormatter,
  createChartTooltipFormatters,
} from '../../utils/chartDataBuilder';
import { ChartType } from '@intelligence/v3/enums/chart';
import {
  DEFAULT_PAGE_SIZE,
  KEY_GAMES_TABLE_TIPS_I18N,
  getDataSourceCustomFieldKey,
  getDataSourceMetricKey,
  getDataSourceMetrics,
} from '../../constants';

import type { ICasualCompetitorsDataSourceProps, ICasualCompetitorsParams } from '../../types';

import type { AxiosRequestConfig } from 'axios';
import type { IStudioCasualCompetitorsMetricsFetchParams } from '@home/types/studio';

import './index.scss';

/**
 * API 方法类型
 */
type TFetchMethod = (
  data: Omit<IStudioCasualCompetitorsMetricsFetchParams, 'group_by' | 'page' | 'page_size'>,
  config?: AxiosRequestConfig,
) => Promise<any>;

/**
 * 模块实例接口
 */
export interface IGamesInstance {
  update: (params: ICasualCompetitorsParams) => void;
  getMetricsList: (showRevenue: boolean) => string[];
}

interface IGamesProps extends IBaseProps, ICasualCompetitorsDataSourceProps {
  keyGamesTableTipsI18n?: IRecord<IRecord<string>>;
  params: ICasualCompetitorsParams | null;
  genreOptionsMap?: IRecord<any[]>;
  fetchTable: TFetchMethod;
  fetchChart: TFetchMethod;
  metricTab: string;
  onMetricTabChange: (value: string) => void;
  chartType?: ChartType;
  onSortStateChange?: (sortState: IRatioMetricSortState<IRecord>) => void;
  onSortedGameIdsChange?: (gameIds: string[]) => void;
}

interface IChartDataItem {
  date: string;
  value: number;
  name: string;
  game_id: string;
  payload?: IRecord;
}

// 折线图展示的最大游戏数量
const CHART_MAX_GAMES = 5;

// 表格指标列表
const TABLE_METRIC_NAMES = ['daily_avg_dau', 'iap_revenue', 'download', 'dau_share'];

// 折线图指标列表
const CHART_METRIC_NAMES = ['daily_avg_dau', 'iap_revenue', 'download'];
const SEGMENT_METRIC_NAMES = ['daily_avg_dau', 'iap_revenue', 'download'];

const Games: React.ForwardRefRenderFunction<IGamesInstance, IGamesProps> = (props, ref) => {
  const {
    style,
    className,
    keyGamesTableTipsI18n = KEY_GAMES_TABLE_TIPS_I18N,
    params,
    genreOptionsMap,
    dataSource,
    fetchTable,
    fetchChart,
    metricTab,
    onMetricTabChange,
    chartType,
    onSortStateChange,
    onSortedGameIdsChange,
  } = props;
  const { locale, formatMessage } = useIntl();

  const { next: nextRequest } = useCancelRequest();
  const defaultDauMetricKey = getDataSourceMetricKey(dataSource, 'daily_avg_dau');
  const subGenreField = getDataSourceCustomFieldKey(dataSource, 'sub_genre');
  const tableMetrics = useMemo(() => {
    return getDataSourceMetrics(dataSource, TABLE_METRIC_NAMES);
  }, [dataSource]);
  const chartMetrics = useMemo(() => {
    return getDataSourceMetrics(dataSource, CHART_METRIC_NAMES);
  }, [dataSource]);

  // 加载状态（初始为 true，由父组件控制加载时机）
  const [tableLoading, setTableLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);

  // 表格数据
  const [gameList, setGameList, getGameList] = useGetState<IRecord[]>([]);

  // 折线图数据（按指标存储）
  const [allChartData, setAllChartData] = useState<Record<string, IChartDataItem[]>>({});

  // 使用 useLocalTableState hook 管理排序分页状态
  const { sorts, pagingQuery, handleSortsChange, handlePagingChange, resetState } = useLocalTableState<IRecord>({
    defaultSorts: createDefaultSorts(dataSource, 'daily_avg_dau'),
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  // 勾选的游戏列表
  const [checkedList, setCheckedList] = useState<string[]>([]);

  const sortedGameList = useMemo(() => {
    if (isEmpty(gameList)) {
      return [];
    }

    if (isEmpty(sorts)) {
      return [...gameList].sort((a, b) => {
        return (b?.[defaultDauMetricKey] ?? 0) - (a?.[defaultDauMetricKey] ?? 0);
      });
    }

    return multiRatioMetricSort([...gameList], sorts);
  }, [defaultDauMetricKey, gameList, sorts]);

  // 从 props 获取 hasRevenue
  const hasRevenue = params?.showRevenue ?? true;

  /**
   * 获取指标列表
   */
  const getMetricsList = (showRevenue: boolean): string[] => {
    return filterRevenueMetrics(tableMetrics, showRevenue);
  };

  /**
   * 获取折线图指标列表
   */
  const getChartMetricsList = (showRevenue: boolean): string[] => {
    return filterRevenueMetrics(chartMetrics, showRevenue);
  };

  const syncSortState = (sortState: IRatioMetricSortState<IRecord> | undefined) => {
    onSortStateChange?.(normalizeMetricSortState(sortState, dataSource));
  };

  const getSortedGameIdsForDownload = (list: IRecord[], sortState?: IRatioMetricSortState<IRecord>): string[] => {
    if (isEmpty(list)) {
      return [];
    }

    const normalizedSortState = normalizeMetricSortState(sortState, dataSource);
    const sortedListForDownload = multiRatioMetricSort([...list], [normalizedSortState]);

    return sortedListForDownload.map((item) => item?.game_id).filter(isNotEmpty);
  };

  const syncSortedGameIds = (list: IRecord[], sortState?: IRatioMetricSortState<IRecord>) => {
    onSortedGameIdsChange?.(getSortedGameIdsForDownload(list, sortState));
  };

  /**
   * 拉取表格数据
   * @param params 请求参数
   * @param sortState 排序状态（传入而非从闭包读取，确保使用最新值）
   * @param param2 请求 token
   */
  const fetchTableData = async (
    params: ICasualCompetitorsParams,
    sortState: IRatioMetricSortState<IRecord> | undefined,
    { cancelToken, isStale }: IRequestToken,
  ) => {
    const { granularity, startDate, endDate, markets, platforms, genreId, showRevenue, revenueScope } = params;
    const metrics = getMetricsList(showRevenue);

    setTableLoading(true);

    try {
      const orderBys = getSortOrderBys(sortState, dataSource);
      const res = await fetchTable(
        {
          start_date: startDate,
          end_date: endDate,
          granularity,
          metrics,
          markets,
          platforms,
          revenue_scope: revenueScope,
          use_custom_fields: ['entity_name', 'cover', 'publisher', 'country_abbre', subGenreField],
          order_by: orderBys,
          filter: {
            genre_id: genreId,
          },
        },
        { cancelToken },
      );

      if (isStale()) {
        return;
      }

      const list = (res as any)?.data?.list || [];
      const entityInfoMap = (res as any)?.ext?.entity_info || {};

      // 合并游戏详情
      const mergedList = list.map((item: any) => {
        const gameId = item?.game_id || '';
        const entityInfo = entityInfoMap?.[gameId] || {};
        return {
          ...item,
          name: entityInfo?.entity_name || '',
          cover: entityInfo?.cover || '',
          publisher: entityInfo?.publisher || '',
          publisher_country_abbre: entityInfo?.country_abbre || '',
          sub_genre: entityInfo?.[subGenreField] || '',
        };
      });

      setGameList(mergedList);
      syncSortedGameIds(mergedList, sortState);
      // 默认勾选前5个游戏
      const defaultChecked = mergedList.slice(0, CHART_MAX_GAMES).map((item: any) => item?.game_id);
      setCheckedList(defaultChecked);
    } catch (error) {
      console.error('fetchTableData error:', error);
      if (isStale()) {
        return;
      }
      setGameList([]);
    } finally {
      if (!isStale()) {
        setTableLoading(false);
      }
    }
  };

  /**
   * 拉取折线图数据（一次性拉取所有指标）
   */
  const fetchAllChartData = async (params: ICasualCompetitorsParams, { cancelToken, isStale }: IRequestToken) => {
    const { granularity, startDate, endDate, markets, platforms, genreId, showRevenue, revenueScope } = params;
    const metrics = getChartMetricsList(showRevenue);

    setChartLoading(true);

    try {
      const gameList = getGameList();
      const gameIds = gameList?.map((game: any) => game?.game_id)?.filter(isNotEmpty) || [];
      if (isEmpty(gameIds)) {
        setAllChartData({});
        return;
      }

      const gameInfoMap = gameList.reduce<Record<string, IRecord>>((acc, game: any) => {
        const gameId = game?.game_id || '';
        if (gameId) {
          acc[gameId] = game;
        }
        return acc;
      }, {});
      // 一次请求拉取所有指标的折线图数据
      const res = await fetchChart(
        {
          start_date: startDate,
          end_date: endDate,
          granularity,
          metrics,
          markets,
          platforms,
          revenue_scope: revenueScope,
          order_by: [],
          filter: {
            genre_id: genreId,
            game_ids: gameIds || [],
          },
        },
        { cancelToken },
      );

      if (isStale()) {
        return;
      }

      const list = (res as any)?.data?.list || [];
      const entityInfoMap = (res as any)?.ext?.entity_info || {};

      // 按指标分类存储数据
      const newChartData: Record<string, IChartDataItem[]> = {};

      metrics.forEach((metricKey) => {
        newChartData[metricKey] = list.map((item: any) => {
          const gameId = item?.game_id || '';
          const entityInfo = entityInfoMap?.[gameId] || {};
          const localGameInfo = gameInfoMap?.[gameId] || {};
          const entityName = entityInfo?.entity_name || localGameInfo?.name || gameId;
          const cover = entityInfo?.cover || localGameInfo?.cover || '';
          return {
            date: item.date || '',
            value: item[metricKey] ?? 0,
            name: entityName,
            game_id: gameId,
            payload: {
              iconUrl: cover,
              iconAlt: entityName,
            },
          };
        });
      });

      setAllChartData(newChartData);
    } catch (error) {
      console.error('fetchAllChartData error:', error);
      if (isStale()) {
        return;
      }
      setAllChartData({});
    } finally {
      if (!isStale()) {
        setChartLoading(false);
      }
    }
  };

  /**
   * 暴露update方法给外层
   */
  useImperativeHandle(ref, () => ({
    update: async (params: ICasualCompetitorsParams) => {
      // IAA品类无收入指标，若当前Tab为iap_revenue则重置为daily_avg_dau
      if (!params.showRevenue && metricTab === 'iap_revenue') {
        onMetricTabChange('daily_avg_dau');
      }

      // 重置勾选列表
      setCheckedList([]);
      setGameList([]);
      setAllChartData({});
      syncSortedGameIds([]);

      // resetState 返回重置后的所有状态，确保接口入参与界面一致
      const { sorts: resetSorts } = resetState();
      syncSortState(resetSorts[0]);

      const requestToken = nextRequest();

      // 并行请求表格数据和折线图数据（传入 resetState 返回的排序值）
      await fetchTableData(params, resetSorts[0], requestToken);

      // 一次性拉取所有指标的折线图数据（使用扩展后的日期范围）
      fetchAllChartData({ ...params, startDate: params.chartStartDate }, requestToken);
    },
    getMetricsList,
  }));

  /**
   * 表格排序变化（包装 hook 方法，增加 Segment Tab 同步逻辑）
   */
  const handleSortsWithTabSync = (newSorts: IRatioMetricSortState<IRecord>[]) => {
    // 同步更新 Segment Tab（排序 metric 即为 tab value）
    if (newSorts.length > 0) {
      const { metric } = newSorts[0];
      if (metric && metric !== metricTab && SEGMENT_METRIC_NAMES.includes(metric)) {
        onMetricTabChange(metric);
      }
    }

    // 排序变化后重新勾选前5个游戏
    const normalizedSortState = normalizeMetricSortState(newSorts[0], dataSource);
    const newCheckedList = getTopNGameIds(gameList, normalizedSortState, CHART_MAX_GAMES, dataSource);
    setCheckedList(newCheckedList);
    syncSortState(normalizedSortState);
    syncSortedGameIds(gameList, normalizedSortState);

    // 调用 hook 提供的方法（已包含排序更新和分页重置）
    handleSortsChange(newSorts);
  };

  /**
   * 勾选变化
   */
  const handleCheckedListChange = (newCheckedList: string[]) => {
    setCheckedList(newCheckedList);
  };

  const granularity = params?.granularity || 'daily';

  const getMetricName = createMetricNameFormatter(granularity, {
    daily: {
      daily_avg_dau: formatMessage({ id: 'dbh_studio_casual_kpi_daily_dau' }),
      iap_revenue: formatMessage({ id: 'dbh_studio_casual_kpi_daily_iap_revenue' }),
      download: formatMessage({ id: 'dbh_studio_casual_col_daily_download' }),
    },
    monthly: {
      daily_avg_dau: formatMessage({ id: 'dbh_studio_casual_kpi_monthly_avg_dau' }),
      iap_revenue: formatMessage({ id: 'dbh_studio_casual_kpi_monthly_iap_revenue' }),
      download: formatMessage({ id: 'dbh_studio_casual_col_monthly_download' }),
    },
  });

  const xAxisLabelFormatter = createXAxisLabelFormatter(granularity);

  const currentMetricKey = getDataSourceMetricKey(dataSource, metricTab);
  const tooltipTimeRange = formatTooltipTimeRange(params?.granularity, params?.startDate, params?.endDate);

  /**
   * 折线图数据：展示勾选的游戏，其余归类为 Others
   */
  const processedChartData = useMemo(() => {
    const rawData = allChartData[currentMetricKey] || [];
    return buildLineChartData({
      rawData,
      checkedIds: checkedList,
      totalCount: sortedGameList.length,
      maxItems: CHART_MAX_GAMES,
      getDate: (item) => item.date,
      getEntityId: (item) => item.game_id || '',
      getName: (item) => item.name,
      getValue: (item) => item.value,
      getIconUrl: (item) => item?.payload?.iconUrl || '',
    }).map((item) => ({
      ...item,
      game_id: item.entityId,
    }));
  }, [allChartData, currentMetricKey, checkedList, sortedGameList]);

  /**
   * Bar/Pie 聚合数据
   */
  const aggregatedChartData = useMemo(() => {
    return buildAggregatedChartData({
      sortedData: sortedGameList,
      checkedIds: checkedList,
      maxItems: CHART_MAX_GAMES,
      getId: (item) => item?.game_id || '',
      getName: (item) => item?.name || '',
      getValue: (item) => item?.[currentMetricKey] ?? 0,
      getIconUrl: (item) => item?.cover || '',
    });
  }, [sortedGameList, checkedList, currentMetricKey]);

  const barChartData = useMemo(() => {
    return aggregatedChartData.map((item) => ({
      name: item.name,
      value: item.value,
      payload: item.payload,
    }));
  }, [aggregatedChartData]);

  const pieChartData = useMemo(() => {
    return aggregatedChartData.map((item) => ({
      id: item.id,
      name: item.name,
      value: item.value,
      payload: item.payload,
    }));
  }, [aggregatedChartData]);

  const { lineTooltipFormatter, barTooltipFormatter, pieTooltipFormatter } = createChartTooltipFormatters({
    getMetricName,
    metricTab,
    timeRange: tooltipTimeRange,
    xAxisLabelFormatter,
  });

  if (chartLoading || tableLoading) {
    return <Loading style={{ height: 803 }} />;
  }

  return (
    <div style={style} className={classnames('dbh-games', className)}>
      {/* 折线图区域 */}
      <ChartSection
        className="dbh-games-chart"
        height={300}
        chartType={chartType || ChartType.Line}
        locale={locale as any}
        metricName={getMetricName(metricTab)}
        barChartData={barChartData}
        pieChartData={pieChartData}
        lineChartData={processedChartData}
        barTooltipFormatter={barTooltipFormatter}
        pieTooltipFormatter={pieTooltipFormatter}
        lineTooltipFormatter={lineTooltipFormatter}
        xAxisLabelFormatter={xAxisLabelFormatter}
      />

      {/* 游戏表格 */}
      <PublisherTable
        showChecker
        showSequence
        isLastColumn
        dataSource={dataSource}
        activeRatioMetricName={formatMessage({ id: 'dbh_studio_casual_genre_col_active_ratio' })}
        tipsI18nKeys={keyGamesTableTipsI18n?.[params?.granularity || 'monthly']}
        granularity={params?.granularity}
        startDate={params?.startDate}
        endDate={params?.endDate}
        loading={tableLoading}
        data={gameList}
        hasRevenue={hasRevenue}
        sorts={sorts}
        onSortsChange={handleSortsWithTabSync}
        pagingQuery={pagingQuery}
        onPagingChange={handlePagingChange}
        checkedList={checkedList}
        onCheckedListChange={handleCheckedListChange}
        genreOptionsMap={genreOptionsMap}
      />
    </div>
  );
};

// @ts-ignore
export default React.forwardRef(Games);
